import type {
  AssignWorkOrderCreateDefaults,
  AssignWorkOrderPreviewPayload,
  AssignWorkOrderPreviewRowContext,
  OrderTypePreviewSummary,
  PropertyPreviewSummary,
  ScheduleEventPreviewSummary,
  WorkOrderPreviewSummary,
} from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown, path: string): JsonObject => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} muss ein Objekt sein.`)
  }

  return value as JsonObject
}

const exactKeys = (value: JsonObject, allowed: readonly string[], path: string) => {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key))

  if (unexpected.length)
    throw new Error(`${path} enthält unbekannte Felder: ${unexpected.join(", ")}.`)
}

const stringValue = (value: unknown, path: string): string => {
  if (typeof value !== "string" || !value.trim())
    throw new Error(`${path} muss ein nicht-leerer String sein.`)

  return value.trim()
}

const nullableString = (value: unknown, path: string): string => {
  if (value === null || value === undefined) return ""

  if (typeof value !== "string") throw new Error(`${path} muss String oder null sein.`)

  return value.trim()
}

const nullableId = (value: unknown, path: string): string | null =>
  nullableString(value, path) || null

const nullableNumber = (value: unknown, path: string): number | null => {
  if (value === null || value === undefined) return null

  if (typeof value !== "number" || !Number.isFinite(value))
    throw new Error(`${path} muss Zahl oder null sein.`)

  return value
}

const stringArray = (value: unknown, path: string): string[] => {
  if (!Array.isArray(value)) throw new Error(`${path} muss ein Array sein.`)

  return value.map((entry, index) => stringValue(entry, `${path}.${index}`))
}

const orderTypeItem = (value: unknown, path: string): WorkOrderOrderTypeItem => {
  const data = objectValue(value, path)
  exactKeys(data, ["order_type_id", "internal_label", "public_label", "description"], path)

  return {
    order_type_id: nullableId(data.order_type_id, `${path}.order_type_id`),
    internal_label: stringValue(data.internal_label, `${path}.internal_label`),
    public_label: stringValue(data.public_label, `${path}.public_label`),
    description: nullableString(data.description, `${path}.description`) || null,
  }
}

const orderTypeItems = (value: unknown, path: string): WorkOrderOrderTypeItem[] => {
  if (!Array.isArray(value)) throw new Error(`${path} muss ein Array sein.`)

  return value.map((entry, index) => orderTypeItem(entry, `${path}.${index}`))
}

const createDefaults = (value: unknown, path: string): AssignWorkOrderCreateDefaults | null => {
  if (value === null || value === undefined) return null

  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "property_id",
      "property_unit_id",
      "order_type_ids",
      "order_types",
      "due_date",
      "estimated_duration_min",
      "allowed_employee_ids",
      "assigned_employee_id",
    ],
    path,
  )

  return {
    propertyId: nullableId(data.property_id, `${path}.property_id`),
    propertyUnitId: nullableId(data.property_unit_id, `${path}.property_unit_id`),
    orderTypeIds: stringArray(data.order_type_ids, `${path}.order_type_ids`),
    orderTypes: orderTypeItems(data.order_types, `${path}.order_types`),
    dueDate: nullableId(data.due_date, `${path}.due_date`),
    estimatedDurationMin: nullableNumber(
      data.estimated_duration_min,
      `${path}.estimated_duration_min`,
    ),
    allowedEmployeeIds: stringArray(data.allowed_employee_ids, `${path}.allowed_employee_ids`),
    assignedEmployeeId: nullableId(data.assigned_employee_id, `${path}.assigned_employee_id`),
  }
}

const rowContext = (value: unknown, path: string): AssignWorkOrderPreviewRowContext => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "candidate_work_order_ids",
      "selected_work_order_id",
      "property_id",
      "property_unit_id",
      "proposal_schedule_event_id",
      "canceled_schedule_event_id",
      "create_work_order_defaults",
    ],
    path,
  )

  return {
    candidateWorkOrderIds: stringArray(
      data.candidate_work_order_ids,
      `${path}.candidate_work_order_ids`,
    ),
    selectedWorkOrderId: nullableId(data.selected_work_order_id, `${path}.selected_work_order_id`),
    propertyId: nullableId(data.property_id, `${path}.property_id`),
    propertyUnitId: nullableId(data.property_unit_id, `${path}.property_unit_id`),
    proposalScheduleEventId: nullableId(
      data.proposal_schedule_event_id,
      `${path}.proposal_schedule_event_id`,
    ),
    canceledScheduleEventId: nullableId(
      data.canceled_schedule_event_id,
      `${path}.canceled_schedule_event_id`,
    ),
    createWorkOrderDefaults: createDefaults(
      data.create_work_order_defaults,
      `${path}.create_work_order_defaults`,
    ),
  }
}

const workOrderSummary = (value: unknown, path: string): WorkOrderPreviewSummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "property_id",
      "property_unit_id",
      "property_unit_label",
      "order_number",
      "order_type_ids",
      "order_type_label",
      "due_date",
      "estimated_duration_min",
      "assigned_employee_id",
      "allowed_employee_ids",
      "current_schedule_event_id",
      "status",
    ],
    path,
  )

  return {
    id: stringValue(data.id, `${path}.id`),
    propertyId: stringValue(data.property_id, `${path}.property_id`),
    propertyUnitId: nullableId(data.property_unit_id, `${path}.property_unit_id`),
    propertyUnitLabel: nullableString(data.property_unit_label, `${path}.property_unit_label`),
    orderNumber: nullableString(data.order_number, `${path}.order_number`),
    orderTypeIds: stringArray(data.order_type_ids, `${path}.order_type_ids`),
    orderTypeLabel: nullableString(data.order_type_label, `${path}.order_type_label`),
    dueDate: nullableId(data.due_date, `${path}.due_date`),
    estimatedDurationMin: nullableNumber(
      data.estimated_duration_min,
      `${path}.estimated_duration_min`,
    ),
    assignedEmployeeId: nullableId(data.assigned_employee_id, `${path}.assigned_employee_id`),
    allowedEmployeeIds: stringArray(data.allowed_employee_ids, `${path}.allowed_employee_ids`),
    currentScheduleEventId: nullableId(
      data.current_schedule_event_id,
      `${path}.current_schedule_event_id`,
    ),
    status: stringValue(data.status, `${path}.status`),
  }
}

const propertySummary = (value: unknown, path: string): PropertyPreviewSummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "street",
      "house_number",
      "postal_code",
      "city",
      "country",
      "address_extra",
      "address_label",
    ],
    path,
  )

  return {
    id: stringValue(data.id, `${path}.id`),
    street: stringValue(data.street, `${path}.street`),
    houseNumber: nullableString(data.house_number, `${path}.house_number`),
    postalCode: nullableString(data.postal_code, `${path}.postal_code`),
    city: nullableString(data.city, `${path}.city`),
    country: nullableString(data.country, `${path}.country`),
    addressExtra: nullableString(data.address_extra, `${path}.address_extra`),
    addressLabel: stringValue(data.address_label, `${path}.address_label`),
  }
}

const scheduleEventSummary = (value: unknown, path: string): ScheduleEventPreviewSummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "work_order_id",
      "property_id",
      "employee_id",
      "employee_name",
      "start_at",
      "end_at",
      "status",
      "flex_type",
      "timeframe_start_at",
      "timeframe_end_at",
      "customer_window_start_at",
      "customer_window_end_at",
    ],
    path,
  )

  return {
    id: stringValue(data.id, `${path}.id`),
    workOrderId: nullableId(data.work_order_id, `${path}.work_order_id`),
    propertyId: nullableId(data.property_id, `${path}.property_id`),
    employeeId: nullableId(data.employee_id, `${path}.employee_id`),
    employeeName: nullableString(data.employee_name, `${path}.employee_name`),
    startAt: stringValue(data.start_at, `${path}.start_at`),
    endAt: stringValue(data.end_at, `${path}.end_at`),
    status: stringValue(data.status, `${path}.status`),
    flexType: nullableId(data.flex_type, `${path}.flex_type`),
    timeframeStartAt: nullableId(data.timeframe_start_at, `${path}.timeframe_start_at`),
    timeframeEndAt: nullableId(data.timeframe_end_at, `${path}.timeframe_end_at`),
    customerWindowStartAt: nullableId(
      data.customer_window_start_at,
      `${path}.customer_window_start_at`,
    ),
    customerWindowEndAt: nullableId(data.customer_window_end_at, `${path}.customer_window_end_at`),
  }
}

const orderTypeSummary = (value: unknown, path: string): OrderTypePreviewSummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    ["id", "internal_label", "public_label", "description", "typical_duration_min"],
    path,
  )

  return {
    id: stringValue(data.id, `${path}.id`),
    internalLabel: stringValue(data.internal_label, `${path}.internal_label`),
    publicLabel: stringValue(data.public_label, `${path}.public_label`),
    description: nullableString(data.description, `${path}.description`),
    typicalDurationMin: nullableNumber(data.typical_duration_min, `${path}.typical_duration_min`),
  }
}

const keyedMap = <T>(
  value: unknown,
  path: string,
  parse: (entry: unknown, entryPath: string) => T & { id: string },
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(objectValue(value, path)).map(([id, entry]) => {
      const parsed = parse(entry, `${path}.${id}`)

      if (parsed.id !== id)
        throw new Error(`${path}.${id}.id stimmt nicht mit dem Map-Key überein.`)

      return [id, parsed]
    }),
  )

export const parseAssignWorkOrderPreview = (payload: unknown): AssignWorkOrderPreviewPayload => {
  const root = objectValue(payload, "payload")
  exactKeys(
    root,
    ["row_context_by_action_case_id", "row_errors_by_action_case_id", "shared"],
    "payload",
  )
  const rows = objectValue(
    root.row_context_by_action_case_id,
    "payload.row_context_by_action_case_id",
  )
  const errors = objectValue(
    root.row_errors_by_action_case_id,
    "payload.row_errors_by_action_case_id",
  )
  const shared = objectValue(root.shared, "payload.shared")
  exactKeys(
    shared,
    ["work_orders_by_id", "properties_by_id", "schedule_events_by_id", "order_types_by_id"],
    "payload.shared",
  )

  const parsed: AssignWorkOrderPreviewPayload = {
    rowContextByActionCaseId: Object.fromEntries(
      Object.entries(rows).map(([id, value]) => [
        id,
        rowContext(value, `payload.row_context_by_action_case_id.${id}`),
      ]),
    ),
    rowErrorsByActionCaseId: Object.fromEntries(
      Object.entries(errors).map(([id, value]) => [
        id,
        stringValue(value, `payload.row_errors_by_action_case_id.${id}`),
      ]),
    ),
    workOrdersById: keyedMap(
      shared.work_orders_by_id,
      "payload.shared.work_orders_by_id",
      workOrderSummary,
    ),
    propertiesById: keyedMap(
      shared.properties_by_id,
      "payload.shared.properties_by_id",
      propertySummary,
    ),
    scheduleEventsById: keyedMap(
      shared.schedule_events_by_id,
      "payload.shared.schedule_events_by_id",
      scheduleEventSummary,
    ),
    orderTypesById: keyedMap(
      shared.order_types_by_id,
      "payload.shared.order_types_by_id",
      orderTypeSummary,
    ),
  }

  const requireReference = (id: string | null, values: Record<string, unknown>, path: string) => {
    if (id && !values[id]) throw new Error(`${path} referenziert die fehlende ID ${id}.`)
  }
  for (const [id, workOrder] of Object.entries(parsed.workOrdersById)) {
    requireReference(
      workOrder.propertyId,
      parsed.propertiesById,
      `payload.shared.work_orders_by_id.${id}.property_id`,
    )
    for (const [index, orderTypeId] of workOrder.orderTypeIds.entries()) {
      requireReference(
        orderTypeId,
        parsed.orderTypesById,
        `payload.shared.work_orders_by_id.${id}.order_type_ids.${index}`,
      )
    }
    requireReference(
      workOrder.currentScheduleEventId,
      parsed.scheduleEventsById,
      `payload.shared.work_orders_by_id.${id}.current_schedule_event_id`,
    )
  }
  for (const [id, event] of Object.entries(parsed.scheduleEventsById)) {
    requireReference(
      event.workOrderId,
      parsed.workOrdersById,
      `payload.shared.schedule_events_by_id.${id}.work_order_id`,
    )
    requireReference(
      event.propertyId,
      parsed.propertiesById,
      `payload.shared.schedule_events_by_id.${id}.property_id`,
    )
  }
  for (const [actionCaseId, context] of Object.entries(parsed.rowContextByActionCaseId)) {
    for (const [index, workOrderId] of context.candidateWorkOrderIds.entries()) {
      requireReference(
        workOrderId,
        parsed.workOrdersById,
        `payload.row_context_by_action_case_id.${actionCaseId}.candidate_work_order_ids.${index}`,
      )
    }
    requireReference(
      context.selectedWorkOrderId,
      parsed.workOrdersById,
      `payload.row_context_by_action_case_id.${actionCaseId}.selected_work_order_id`,
    )
    requireReference(
      context.propertyId,
      parsed.propertiesById,
      `payload.row_context_by_action_case_id.${actionCaseId}.property_id`,
    )
    requireReference(
      context.proposalScheduleEventId,
      parsed.scheduleEventsById,
      `payload.row_context_by_action_case_id.${actionCaseId}.proposal_schedule_event_id`,
    )
    requireReference(
      context.canceledScheduleEventId,
      parsed.scheduleEventsById,
      `payload.row_context_by_action_case_id.${actionCaseId}.canceled_schedule_event_id`,
    )
    requireReference(
      context.createWorkOrderDefaults?.propertyId ?? null,
      parsed.propertiesById,
      `payload.row_context_by_action_case_id.${actionCaseId}.create_work_order_defaults.property_id`,
    )
    for (const [index, orderTypeId] of (
      context.createWorkOrderDefaults?.orderTypeIds ?? []
    ).entries()) {
      requireReference(
        orderTypeId,
        parsed.orderTypesById,
        `payload.row_context_by_action_case_id.${actionCaseId}.create_work_order_defaults.order_type_ids.${index}`,
      )
    }
  }

  return parsed
}
